import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Briefcase, Clock, ArrowRight, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import CaptchaChallenge from '../components/CaptchaChallenge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CareersPage() {
  const { t, getLocalizedContent } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cover_letter: '',
    cv_base64: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [fileName, setFileName] = useState('');
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const handleCaptchaValidate = (isValid, token) => {
    setCaptchaValid(isValid);
    setCaptchaToken(token);
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Le fichier ne doit pas dépasser 5MB');
        return;
      }
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = () => {
        setFormData({ ...formData, cv_base64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!selectedJob) return;
    
    if (!captchaValid) {
      toast.error('Veuillez compléter la vérification anti-spam');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/jobs/${selectedJob.id}/apply`, {
        ...formData,
        timestamp: Math.floor(Date.now() / 1000),
        captcha_token: captchaToken
      });
      toast.success('Candidature envoyée avec succès !');
      setShowApplyModal(false);
      setFormData({ name: '', email: '', phone: '', cover_letter: '', cv_base64: '' });
      setFileName('');
      setSelectedJob(null);
      setCaptchaValid(false);
      setCaptchaToken(null);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la candidature');
    } finally {
      setSubmitting(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div data-testid="careers-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <span className="inline-block px-4 py-1 bg-[#0B6AB3]/20 text-[#0B6AB3] text-sm font-medium rounded-full mb-4">
              Opportunités de carrière
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-white font-['Montserrat']">
              {t('jobs_title')}
            </h1>
            <p className="text-gray-300 mt-4 max-w-2xl mx-auto text-lg">
              {t('jobs_subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Jobs List */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-20">
              <Briefcase className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Aucune offre d'emploi disponible pour le moment</p>
              <p className="text-gray-400 mt-2">Revenez bientôt pour découvrir nos opportunités</p>
            </div>
          ) : (
            <div className="space-y-6">
              {jobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300" data-testid={`job-card-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-xl text-[#0F172A] mb-2 font-['Montserrat']">
                            {getLocalizedContent(job.title)}
                          </h3>
                          <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                            <span className="flex items-center">
                              <MapPin className="h-4 w-4 mr-1 text-[#0B6AB3]" />
                              {job.location}
                            </span>
                            <span className="flex items-center">
                              <Briefcase className="h-4 w-4 mr-1 text-[#0B6AB3]" />
                              {job.contract_type}
                            </span>
                            {job.deadline && (
                              <span className="flex items-center">
                                <Calendar className="h-4 w-4 mr-1 text-[#0B6AB3]" />
                                {new Date(job.deadline).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </div>
                          <p className="mt-3 text-gray-600 line-clamp-2">
                            {getLocalizedContent(job.description)}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <Button
                            onClick={() => {
                              setSelectedJob(job);
                              setShowApplyModal(true);
                            }}
                            className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white"
                            data-testid={`apply-btn-${index}`}
                          >
                            {t('jobs_apply')}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Apply Modal */}
      <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="apply-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0F172A]">
              {t('jobs_apply')}
            </DialogTitle>
          </DialogHeader>
          {selectedJob && (
            <div className="mt-2 mb-4 p-3 bg-[#F8F9FA] rounded-lg">
              <p className="font-medium text-[#0F172A]">{getLocalizedContent(selectedJob.title)}</p>
              <p className="text-sm text-gray-600">{selectedJob.location} • {selectedJob.contract_type}</p>
            </div>
          )}
          <form onSubmit={handleApply} className="space-y-4">
            <div>
              <Label htmlFor="name">{t('contact_name')} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="apply-name"
              />
            </div>
            <div>
              <Label htmlFor="email">{t('contact_email')} *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="apply-email"
              />
            </div>
            <div>
              <Label htmlFor="phone">{t('contact_phone')} *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                data-testid="apply-phone"
              />
            </div>
            <div>
              <Label htmlFor="cover_letter">Lettre de motivation</Label>
              <Textarea
                id="cover_letter"
                value={formData.cover_letter}
                onChange={(e) => setFormData({ ...formData, cover_letter: e.target.value })}
                rows={4}
                placeholder="Décrivez votre motivation pour ce poste..."
                data-testid="apply-cover-letter"
              />
            </div>
            <div>
              <Label htmlFor="cv">CV (PDF, max 5MB)</Label>
              <div className="mt-1">
                <label
                  htmlFor="cv"
                  className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#0B6AB3] transition-colors"
                >
                  <Upload className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">
                    {fileName || 'Cliquez pour télécharger votre CV'}
                  </span>
                  <input
                    id="cv"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    onChange={handleFileChange}
                    data-testid="apply-cv"
                  />
                </label>
              </div>
            </div>
            {/* Captcha Challenge */}
            <CaptchaChallenge onValidate={handleCaptchaValidate} />
            {/* Honeypot field */}
            <input
              type="text"
              name="website"
              style={{ display: 'none' }}
              tabIndex={-1}
              autoComplete="off"
            />
            <Button
              type="submit"
              className="w-full bg-[#0B6AB3] hover:bg-[#095c9e] text-white"
              disabled={submitting || !captchaValid}
              data-testid="apply-submit"
            >
              {submitting ? 'Envoi en cours...' : 'Envoyer ma candidature'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
